import { useEffect, useRef, useState, type RefObject } from 'react'

import type { AllListingsListing } from '@b-stock/search-api-client'

import { useViewItemList } from './useViewItemList'

interface UseViewItemListOnVisibleOptions {
  /** Ref to the carousel container element to observe for viewport visibility */
  containerRef: RefObject<HTMLElement | null>
  listings: AllListingsListing[]
  itemListId: string
  itemListName: string
  currency?: string
  maxItems?: number

  /** Whether tracking is enabled (defaults to true, set to false during loading states) */
  enabled?: boolean

  /** Intersection threshold - percentage of element that must be visible (defaults to 0.5 for 50%) */
  threshold?: number
}

/**
 * Custom hook to track GTM view_item_list events when a carousel becomes visible in the viewport.
 * Uses Intersection Observer API to detect when the carousel container is 50% visible.
 * Fires only once per component mount to track meaningful product impressions.
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null)
 *
 * useViewItemListOnVisible({
 *   containerRef,
 *   listings,
 *   itemListId: 'carousel-watchlist-homepage',
 *   itemListName: 'Your Watchlist',
 *   enabled: !isLoading && listings.length > 0,
 * })
 *
 * return <Section ref={containerRef}>...</Section>
 * ```
 */
export function useViewItemListOnVisible({
  containerRef,
  listings,
  itemListId,
  itemListName,
  currency = 'USD',
  maxItems = 50,
  enabled = true,
  threshold = 0.5,
}: UseViewItemListOnVisibleOptions) {
  const [isVisible, setIsVisible] = useState(false)
  const hasTracked = useRef(false)

  // Intersection Observer to detect when container is visible in viewport
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting && !hasTracked.current) {
          setIsVisible(true)
        }
      },
      {
        threshold,
        rootMargin: '0px',
      }
    )

    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [containerRef, threshold])

  // Fire the view_item_list event when carousel becomes visible
  // Use a controlled approach: only enable useViewItemList when visible and haven't tracked yet
  const shouldTrack =
    // oxlint-disable-next-line react/refs
    isVisible && !hasTracked.current && enabled && listings.length > 0

  useViewItemList({
    listings,
    itemListId,
    itemListName,
    currency,
    maxItems,
    // oxlint-disable-next-line react/refs
    enabled: shouldTrack,
  })

  // Ensuring we fire only once per mount
  useEffect(() => {
    // oxlint-disable-next-line react/refs
    if (shouldTrack) {
      hasTracked.current = true
    }
    // oxlint-disable-next-line react/refs
  }, [shouldTrack])
}
